import { useCallback } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";

import { generateChatTitle, postMessage, renameChat } from "~/entities/chat";
import { chatDetailKey, chatMessagesKey } from "~/entities/chat/useActiveChat";
import { elementKeys } from "~/entities/element";
import { chatListKey } from "~/entities/chat/useChats";
import { useActiveWorkspace } from "~/entities/workspace";
import { toDisplayPrompt } from "~/shared/lib/element-mentions";
import { promptToFallbackTitle } from "~/shared/lib/fallback-title";
import { useDraftChatStore } from "~/shared/store/useDraftChatStore";

import type { AttachmentCreate, Chat, ChatMessage } from "~/entities/chat";
import type { ElementListResponse } from "~/entities/element";

/**
 * Имя чату даёт человекочитаемый текст, а не хранимый: в промпте упоминание
 * элемента лежит ссылкой `@[element:<uuid>]`, и без подстановки чат назывался бы
 * этим uuid — и в сайдбаре, и в хлебных крошках. Имена берём из кеша библиотеки
 * (её уже загрузил промпт-бар); чего в кеше нет, `toDisplayPrompt` вырежет —
 * заголовок из остатка фразы всё равно осмысленнее ссылки.
 */
function promptForTitle(qc: QueryClient, prompt: string, workspaceId: string | undefined): string {
    const library = qc.getQueryData<ElementListResponse>(elementKeys.list(workspaceId));
    // Промпт из одних упоминаний неизвестных элементов свернулся бы в пустую
    // строку — тогда называть чат нечем, и берём исходный текст как раньше.
    return toDisplayPrompt(prompt, library?.items ?? []) || prompt;
}

// Гарантирует имя чата: пытается LLM-title, иначе fallback из промпта.
// Появление чата в сайдбаре завязано именно на инвалидацию chatListKey
// внутри этой функции — пока имени нет, чата в списке не видно.
async function maybeAutoRename(
    qc: QueryClient,
    chatId: string,
    prompt: string,
    workspaceId: string | undefined,
): Promise<void> {
    const cached = qc.getQueryData<Chat>(chatDetailKey(chatId));
    if (cached?.title) return;

    const readable = promptForTitle(qc, prompt, workspaceId);
    let finalTitle: string;
    try {
        const { title, fallback } = await generateChatTitle(readable);
        finalTitle = fallback ? promptToFallbackTitle(readable) : title;
    } catch {
        finalTitle = promptToFallbackTitle(readable);
    }

    try {
        const updated = await renameChat(chatId, finalTitle);
        qc.setQueryData<Chat>(chatDetailKey(chatId), updated);
    } catch {
        // PATCH упал — кладём fallback-title локально, чтобы юзер всё равно
        // видел чат в сайдбаре под именем. На бэке title останется null;
        // при следующем сообщении в этом чате попытка повторится
        // (cached.title будет undefined после refresh).
        const localChat = {
            ...(cached ?? ({} as Chat)),
            id: chatId,
            title: finalTitle,
        } as Chat;
        qc.setQueryData<Chat>(chatDetailKey(chatId), localChat);
    }
    if (workspaceId) {
        void qc.invalidateQueries({ queryKey: chatListKey(workspaceId) });
    }
}

// Submit user-сообщения через реальный POST /v2/chats/{id}/messages.
// Никакого optimistic bubble — сообщение появляется в ленте только
// после ответа сервера, чтобы createdAt и прочие поля были из API,
// а не клиентскими. Возвращаем созданное сообщение, чтобы вызывающий код
// (ChatShell) сразу мог запустить генерацию по message_id.
export function useSubmitLocalMessage() {
    const qc = useQueryClient();
    const dropChatFromDrafts = useDraftChatStore((s) => s.dropChatFromDrafts);
    const { activeWorkspaceId } = useActiveWorkspace();

    return useCallback(
        async (
            chatId: string,
            prompt: string,
            attachments: AttachmentCreate[],
        ): Promise<ChatMessage | null> => {
            const message = await postMessage(chatId, {
                prompt,
                role: 1,
                attachments: attachments.length > 0 ? attachments : undefined,
            });
            // Промоутим черновик в живой чат, НО pending-вложения и текст
            // промпта НЕ очищаем — остаются в композере для быстрой повторной
            // генерации. Полная очистка вложений — только по явному
            // действию юзера (removePending / смена чата).
            dropChatFromDrafts(chatId);
            void qc.invalidateQueries({ queryKey: chatMessagesKey(chatId) });
            // chatListKey намеренно не инвалидируем здесь — это сделает
            // maybeAutoRename после успеха (или fallback'а) на rename'е.
            void maybeAutoRename(qc, chatId, prompt, activeWorkspaceId ?? undefined);
            return message;
        },
        [qc, dropChatFromDrafts, activeWorkspaceId],
    );
}
